<template>
  <div v-if="isActive" class="d-bottom-sheet modal is-active">
    <transition name="fade">
      <div v-if="isVisible" key="outside" class="full-overlay" @click="cancel('outside')" />
    </transition>
    <transition :name="animation">
      <div v-if="isVisible" class="modal-card animation-content">
        <section class="modal-card-body">
          <component :is="component" v-if="component" v-bind="props" v-on="events" @close="close" />
          <div v-else-if="content" v-html="content" />
          <slot v-else />
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
  import { removeElement } from '../../../utils/helpers';
  /**
  * @displayName DBottomSheet
  */
  export default {
    name: 'DBottomSheet',

    props: {
      props: Object,
      events: Object,

      programmatic: {
        type: Boolean,
        default: false,
      },

      /**
       * Should be paired with active.sync.
       */
      active: {
        type: Boolean,
        default: false,
      },

      /**
       * Pass a component (if programmatic)
       */
      component: [Object, Function],

      /**
       * Displays only raw HTML/text passed through the prop
       */
      content: String,

      /**
       * Any animation can be used here. It is always recommends to use `slide-up` when possible.
       */
      animation: {
        type: String,
        default: 'slide-up',
      },

      /**
       * A user can cancel the sheet by pressing the ESC key or clicking outside.
       */
      canCancel: {
        type: [Array, Boolean],
        default: () => ['escape', 'outside'],
      },

      /**
       * Pass a function when cancel is triggered.
       */
      onCancel: {
        type: Function,
        default: () => { },
      },

      /**
       * Allow background scroll
       */
      bgScroll: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        isActive: this.active || false,
        isVisible: false,
        savedScrollTop: null,
        newWidth: typeof this.width === 'number' ? `${this.width}px` : this.width,
      };
    },

    computed: {
      cancelOptions() {
        if (typeof this.canCancel !== 'boolean') {
          return this.canCancel;
        }

        return this.canCancel ? ['escape', 'outside'] : [];
      },
    },

    watch: {
      active(value) {
        this.isActive = value;
      },
      isActive(val) {
        this.handleScroll();
        if (val) {
          this.doShow();
        }
      },
    },

    created() {
      if (typeof window !== 'undefined') {
        document.addEventListener('keyup', this.keyPress);
      }
    },

    beforeMount() {
      // Insert the Modal component in body tag
      // only if it's programmatic
      this.programmatic && document.body.appendChild(this.$el);
    },

    mounted() {
      if (this.programmatic) this.isActive = true;
      else if (this.isActive) this.handleScroll();
    },

    beforeDestroy() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('keyup', this.keyPress);
        // reset scroll
        document.documentElement.classList.remove('is-clipped');
        const savedScrollTop = !this.savedScrollTop
          ? document.documentElement.scrollTop
          : this.savedScrollTop;
        document.body.classList.remove('is-noscroll');
        document.documentElement.scrollTop = savedScrollTop;
        document.body.style.top = null;
      }
    },

    methods: {
      doShow() {
        // We do this in `$nextTick()` to ensure the modal is in DOM first before we show it
        this.$nextTick(() => {
          this.isVisible = true;
        });
      },

      handleScroll() {
        if (typeof window === 'undefined') return;
        if (this.bgScroll) return;

        this.savedScrollTop = !this.savedScrollTop
          ? document.documentElement.scrollTop
          : this.savedScrollTop;
        if (this.isActive) {
          document.body.classList.add('is-noscroll');
        } else {
          document.body.classList.remove('is-noscroll');
        }
        if (this.isActive) {
          document.body.style.top = `-${this.savedScrollTop}px`;
          return;
        }
        document.documentElement.scrollTop = this.savedScrollTop;
        document.body.style.top = null;
        this.savedScrollTop = null;
      },
      /**
       * Close the Modal if canCancel and call the onCancel prop (function).
       */
      cancel(method) {
        if (this.cancelOptions.indexOf(method) < 0) return;
        this.onCancel.apply(null, arguments);
        this.close();
      },
      /**
       * Call the onCancel prop (function).
       * Emit events, and destroy modal if it's programmatic.
       */
      close() {
        this.isVisible = false;
        setTimeout(() => {
          this.$nextTick(() => {
            this.$emit('close');
            this.$emit('update:active', false);
            // Timeout for the animation complete before destroying
            if (this.programmatic) {
              this.isActive = false;
              setTimeout(() => {
                this.$destroy();
                removeElement(this.$el);
              }, 150);
            }
          });
        }, 225);
      },
      /**
       * Keypress event that is bound to the document.
       */
      keyPress(event) {
        // Esc key
        if (this.isActive && event.keyCode === 27) this.cancel('escape');
      },
    },
  };
</script>